import { LayoutDashboard, DraftingCompass, BrainCircuit, Network, Workflow, CheckCircle2 } from "lucide-react";
import { FadeUp, SectionHeading, CTASection } from "../components/Shared";
import { IMAGES } from "../lib/brand";

const FEATURES = [
  {
    icon: LayoutDashboard,
    title: "Customer Portal",
    tag: "PORTAL",
    desc: "A single secure workspace for your team to manage RFQs, orders, revisions and documentation.",
    points: ["Order history & repeat ordering", "Revision-controlled CAD uploads", "Quality certificates & test reports", "Team roles and approvals"],
  },
  {
    icon: DraftingCompass,
    title: "Engineering Hub",
    tag: "ENGINEERING",
    desc: "Every project is reviewed by our engineering team for manufacturability before production begins.",
    points: ["DFM analysis on every part", "Material & process recommendations", "Tolerance and finish consultation", "Cost-reduction engineering"],
  },
  {
    icon: BrainCircuit,
    title: "AI-Powered Instant Quote",
    tag: "QUOTING",
    desc: "Geometry-aware pricing engines analyze your CAD models to generate accurate quotes in hours, not weeks.",
    points: ["Automated geometry analysis", "Multi-process price comparison", "Lead-time options per quote", "Transparent cost breakdown"],
  },
  {
    icon: Network,
    title: "Manufacturing Network",
    tag: "NETWORK",
    desc: "A vetted, audited network of precision manufacturers matched to your part's exact requirements.",
    points: ["Capability-based smart matching", "Audited quality systems", "Redundant capacity for scale", "Domestic & global options"],
  },
  {
    icon: Workflow,
    title: "Production Workflow",
    tag: "PRODUCTION",
    desc: "Full visibility from purchase order to delivery with milestone tracking and quality gates.",
    points: ["Live production milestones", "In-process quality checkpoints", "Logistics & customs handling", "On-time delivery analytics"],
  },
];

export default function Platform() {
  return (
    <div data-testid="platform-page">
      <section className="relative py-28 sm:py-36 bg-slate-950 overflow-hidden">
        <img src={IMAGES.abstractData} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 to-slate-950" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
          <FadeUp>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-400 mb-6 font-mono-tech">The Platform</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white max-w-3xl leading-[1.05]">
              A digital operating system for <span className="font-medium text-blue-400">custom manufacturing.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">
              Five integrated modules take your part from concept to your loading dock — with engineering rigor at every step.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-6">
          {FEATURES.map((f, i) => (
            <FadeUp key={f.title} delay={0.05}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 p-8 sm:p-10 rounded-xl bg-card border border-border transition-transform duration-200 hover:-translate-y-1" data-testid={`platform-feature-${i}`}>
                <div className="md:col-span-5">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <f.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <span className="font-mono-tech text-[11px] font-bold tracking-[0.25em] text-accent">{f.tag} / 0{i + 1}</span>
                  </div>
                  <h3 className="mt-6 text-2xl sm:text-3xl font-medium font-heading">{f.title}</h3>
                  <p className="mt-4 text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
                <div className="md:col-span-7 md:border-l md:border-border md:pl-10 grid sm:grid-cols-2 gap-4 content-center">
                  {f.points.map((p) => (
                    <div key={p} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-sm text-foreground/90">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <CTASection
        title="See the platform in action."
        description="Talk to our team about onboarding your next project onto Vectron's digital manufacturing workflow."
      />
    </div>
  );
}
